import { createElement } from "../utils/element-handlers.utils";
import NobuVideo from "./nobu-video";

type VideoFrameControlOptions = {
    playPauseControl?: boolean,
    muteUnmuteControl?: boolean
};

const playpauseControlAttributeName = 'data-video-frame-control-playpause';
const muteunmuteControlAttributeName = 'data-video-frame-control-muteunmute';

class NobuVideoFrame {
    private videoFrameElement: HTMLElement;
    private videoFrameControlsElement: HTMLElement | null = null;
    private videoFrameControlPlayPauseElement: HTMLElement | null = null;
    private videoFrameControlMuteUnmuteElement: HTMLElement | null = null;
    
    public videoInstance: NobuVideo;
    public videoElement: HTMLVideoElement;

    constructor(
        videoFrameElement: HTMLElement,
        createControls: boolean | VideoFrameControlOptions = false
    ) {
        const videoFrameVideoElement = videoFrameElement.querySelector('video.n-video-frame-video');
        if (!videoFrameVideoElement) throw 'NobuVideoFrame: HTMLVideoElement is mandatory';

        this.videoFrameElement = videoFrameElement;
        this.videoElement = videoFrameVideoElement as HTMLVideoElement;
        this.videoInstance = new NobuVideo(this.videoElement);
        this.init(createControls);
    }

    private init(createControls: boolean | VideoFrameControlOptions) {
        if (createControls) {
            this.createControls(
                (createControls === true)
                    ? {
                        playPauseControl: true,
                        muteUnmuteControl: true
                    }
                    : (createControls || {})
            );
        }
        else {
            this.findControls();
        }

        if (this.videoFrameControlPlayPauseElement) {
            this.setPlayPauseButton(this.videoFrameControlPlayPauseElement);
        }

        if (this.videoFrameControlMuteUnmuteElement) {
            this.setMuteUnmuteButton(this.videoFrameControlMuteUnmuteElement);
        }
    }

    private createControls(controlOptions: VideoFrameControlOptions) {
        const controlsToAdd: HTMLElement[] = [];

        if (controlOptions.playPauseControl) {
            this.videoFrameControlPlayPauseElement = createElement('button', {
                className: 'n-video-frame-control',
                [playpauseControlAttributeName]: playpauseControlAttributeName
            }, [
                createElement('span', 'n-video-frame-control-label', [
                    createElement('img', {
                        src: './themes/nobu/images/icon-play.svg',
                        alt: 'Play video'
                    }),
                    'Play video'
                ]),
                createElement('span', 'n-video-frame-control-label', [
                    createElement('img', {
                        src: './themes/nobu/images/icon-pause.svg',
                        alt: 'Pause video'
                    }),
                    'Pause video'
                ])
            ]);

            controlsToAdd.push(this.videoFrameControlPlayPauseElement);
        }

        if (controlOptions.muteUnmuteControl) {
            this.videoFrameControlMuteUnmuteElement = createElement('button', {
                className: 'n-video-frame-control',
                [muteunmuteControlAttributeName]: muteunmuteControlAttributeName
            }, [
                createElement('span', 'n-video-frame-control-label', [
                    createElement('img', {
                        src: './themes/nobu/images/icon-unmuted.svg',
                        alt: 'Unmuted video'
                    })
                ]),
                createElement('span', 'n-video-frame-control-label', [
                    createElement('img', {
                        src: './themes/nobu/images/icon-muted.svg',
                        alt: 'Muted video'
                    })
                ])
            ]);

            controlsToAdd.push(this.videoFrameControlMuteUnmuteElement);
        }


        this.videoFrameControlsElement = createElement(
            'div',
            'n-video-frame-controls',
            controlsToAdd
        );
        this.videoFrameElement.appendChild(this.videoFrameControlsElement);
    }

    private findControls() {
        this.videoFrameControlsElement = this.videoFrameElement.querySelector('div.n-video-frame-controls');

        if (this.videoFrameControlsElement) {
            this.videoFrameControlPlayPauseElement = this.videoFrameControlsElement.querySelector('button.n-video-frame-control[' + playpauseControlAttributeName + ']');
            this.videoFrameControlMuteUnmuteElement = this.videoFrameControlsElement.querySelector('button.n-video-frame-control[' + muteunmuteControlAttributeName + ']');
        }
    }

    private setPlayPauseButton(buttonElement: HTMLElement) {
        const self = this;

        setPlaypauseAttributeValue();
        self.videoInstance.onUpdate(setPlaypauseAttributeValue);

        buttonElement.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();

            if (self.videoInstance.isPlaying()) {
                self.videoInstance.pause();
            }
            else {
                self.videoInstance.play();
            }
        });

        function setPlaypauseAttributeValue() {
            if (self.videoInstance.isPlaying()) {
                buttonElement.setAttribute(playpauseControlAttributeName, 'playing');
            }
            else {
                buttonElement.setAttribute(playpauseControlAttributeName, 'paused');
            }
        }
    }

    private setMuteUnmuteButton(buttonElement: HTMLElement) {
        const self = this;

        setMuteunmuteAttributeValue();
        self.videoInstance.onUpdate(setMuteunmuteAttributeValue);

        buttonElement.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();

            if (self.videoInstance.isMuted()) {
                self.videoInstance.unmute();
            }
            else {
                self.videoInstance.mute();
            }
        });

        function setMuteunmuteAttributeValue() {
            if (self.videoInstance.isMuted()) {
                buttonElement.setAttribute(muteunmuteControlAttributeName, 'muted');
            }
            else {
                buttonElement.setAttribute(muteunmuteControlAttributeName, 'unmuted');
            }
        }
    }
}

export default NobuVideoFrame;
